import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { RefObject } from 'react';

import { ErrorScreen } from './ErrorScreen';
import { LoginPage } from './LoginPage';
import { StorageAccessPage } from './StorageAccessPage';
import { type UrlProps, useUrl } from './useUrl';

import { useValidationState, Subjects, LoadingStatus } from './useValidationState';
import { type NavigationPolicy } from '../navigation-policy';

export type ChildrenProps = {
	handleContentLoadStarted: () => void;
	handleIframeOnLoad: () => void;
	handleIframeUnload: () => void;
	iframeLoaded?: boolean;
	iframeRef: RefObject<HTMLIFrameElement>;
	isLoading?: boolean;
	navigationPolicy?: NavigationPolicy;
	redirectedToLogin?: boolean;
};

export type Props = UrlProps & {
	children: (props: ChildrenProps) => React.ReactElement;
	navigationPolicy?: NavigationPolicy;
};

type CrossDomainErrorValidationProps = Pick<
	Props,
	'allowedFeatures' | 'children' | 'contentId' | 'navigationPolicy' | 'parentProduct' | 'spaceKey'
> & {
	embeddedConfluenceUrl: ReturnType<typeof useUrl>['embeddedConfluenceUrl'];
	standaloneConfluenceUrl: ReturnType<typeof useUrl>['standaloneConfluenceUrl'];
};

const noop = () => {};

const IFRAME_LOAD_TIMEOUT_MS = 2000;

export const PageLoadValidation = (
	props: Props,
): string | number | boolean | Iterable<React.ReactNode> | React.JSX.Element | null | undefined => {
	const { standaloneConfluenceUrl, embeddedConfluenceUrl } = useUrl(props);
	const {
		state: { contentLoadStarted, iframeLoaded },
	} = useValidationState();

	const isEmbeddedUrlOnSameDomain = embeddedConfluenceUrl
		? window.location.origin === new URL(embeddedConfluenceUrl).origin
		: false;

	const unusedIframeRef = useRef<HTMLIFrameElement>(null);

	return isEmbeddedUrlOnSameDomain ? (
		props.children({
			isLoading: (contentLoadStarted && !iframeLoaded) || false, // if same domain, we bypass loading screen in favor of internal iframe loading experience
			iframeLoaded: iframeLoaded || true, // if same domain, set to true so iframe can handle loading experience
			iframeRef: unusedIframeRef,
			handleIframeOnLoad: noop,
			handleContentLoadStarted: noop,
			handleIframeUnload: noop,
			navigationPolicy: props.navigationPolicy,
		})
	) : (
		<CrossDomainErrorValidation
			key={embeddedConfluenceUrl || props.contentId}
			standaloneConfluenceUrl={standaloneConfluenceUrl}
			embeddedConfluenceUrl={embeddedConfluenceUrl}
			allowedFeatures={props.allowedFeatures}
			{...props}
		/>
	);
};

const CrossDomainErrorValidation = ({
	allowedFeatures,
	children,
	contentId,
	embeddedConfluenceUrl,
	navigationPolicy,
	parentProduct,
	spaceKey,
	standaloneConfluenceUrl,
}: CrossDomainErrorValidationProps) => {
	const { state, dispatch } = useValidationState();

	const {
		iframeLoaded,
		contentLoadStarted,
		shouldAskStorageAccess,
		shouldAskToLogin,
		redirectedToLogin,
		iframeUnloaded,
	} = state;

	const iframeRef = useRef<HTMLIFrameElement>(null);
	const iframeOnLoadFiredRef = useRef(false);
	const iframeOnLoadSrcRef = useRef<string | null>(null);
	const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

	const dispatchIframeLoaded = useCallback(() => {
		dispatch({
			subject: Subjects.CONTENT_LOAD,
			status: LoadingStatus.IFRAME_LOADED,
		});
	}, [dispatch]);

	const clearPendingIframeLoadTimeout = useCallback(() => {
		if (timeoutIdRef.current) {
			clearTimeout(timeoutIdRef.current);
			timeoutIdRef.current = null;
		}
	}, []);

	const resetIframeLoadTracking = useCallback(() => {
		iframeOnLoadFiredRef.current = false;
		iframeOnLoadSrcRef.current = null;
		clearPendingIframeLoadTimeout();
	}, [clearPendingIframeLoadTimeout]);

	const handleIframeOnLoad = useCallback(() => {
		/* Record that the iframe onLoad event has fired and store the current src.
		   We verify the iframe src to ensure we only handle the correct iframe,
		   as the same Iframe component is also used by the StorageAccessPage component,
		   and the src may change during transitions between view/edit modes or other redirects.
		*/
		if (!iframeRef.current) {
			return;
		}

		iframeOnLoadFiredRef.current = true;
		iframeOnLoadSrcRef.current = iframeRef.current.src;

		// If content has already started loading, dispatch immediately
		if (contentLoadStarted && !iframeLoaded) {
			dispatchIframeLoaded();
			return;
		}

		// If content hasn't started loading yet, set a timeout to dispatch after a delay
		// This handles the case where content fails to load or never sends the start message
		if (!contentLoadStarted && !iframeLoaded) {
			clearPendingIframeLoadTimeout();
			timeoutIdRef.current = setTimeout(() => {
				// Verify the iframe hasn't changed before dispatching
				if (
					iframeOnLoadFiredRef.current &&
					iframeRef.current?.src === iframeOnLoadSrcRef.current &&
					!iframeLoaded
				) {
					dispatchIframeLoaded();
				}
			}, IFRAME_LOAD_TIMEOUT_MS);
		}
	}, [contentLoadStarted, iframeLoaded, dispatchIframeLoaded, clearPendingIframeLoadTimeout]);

	// As soon as both the iframe onLoad has fired AND content has started loading, dispatch the IFRAME_LOADED action.
	useEffect(() => {
		if (
			iframeOnLoadFiredRef.current &&
			contentLoadStarted &&
			iframeRef.current?.src === iframeOnLoadSrcRef.current &&
			!iframeLoaded
		) {
			clearPendingIframeLoadTimeout();
			dispatchIframeLoaded();
		}
	}, [clearPendingIframeLoadTimeout, contentLoadStarted, dispatchIframeLoaded, iframeLoaded]);

	useEffect(() => {
		if (!contentLoadStarted && !iframeLoaded) {
			resetIframeLoadTracking();
		}
	}, [contentLoadStarted, iframeLoaded, resetIframeLoadTracking]);

	useEffect(
		() => () => {
			clearPendingIframeLoadTimeout();
		},
		[clearPendingIframeLoadTimeout],
	);

	const handleContentLoadStarted = useCallback(() => {
		dispatch({
			subject: Subjects.CONTENT_LOAD,
			status: LoadingStatus.CONTENT_LOAD_STARTED,
		});
	}, [dispatch]);

	const handleIframeUnload = useCallback(() => {
		dispatch({
			subject: Subjects.CONTENT_LOAD,
			status: LoadingStatus.IFRAME_UNLOADED,
		});
	}, [dispatch]);

	const isLoginFlowDisabled = useMemo(
		() => allowedFeatures?.includes('disable-login-flow') ?? false,
		[allowedFeatures],
	);

	const reloadEP = useCallback(() => {
		dispatch({
			subject: Subjects.CONTENT_LOAD,
			status: LoadingStatus.RELOAD_EP,
		});
	}, [dispatch]);

	const failToLoad = useMemo(
		() =>
			(iframeLoaded && !contentLoadStarted) ||
			/*
      If anonymous access is permitted globally but restricted on a content's parent space,
      the iframe will attempt a login redirect which we intercept.
      To avoid an error in the iframe, we need to wait for the iframe window to unload
      before unmouting the Iframe component.
    */
			(iframeUnloaded && redirectedToLogin),
		[contentLoadStarted, iframeLoaded, iframeUnloaded, redirectedToLogin],
	);

	const pageNavigationPolicy = useMemo(
		(): NavigationPolicy => ({
			navigate(url, modifiers, defaultNavigate) {
				if (url.includes('/login?application=confluence')) {
					dispatch({
						subject: Subjects.CONTENT_LOAD,
						status: LoadingStatus.REDIRECTED_TO_LOGIN,
					});
					return;
				}
				return navigationPolicy?.navigate
					? navigationPolicy.navigate(url, modifiers, defaultNavigate)
					: defaultNavigate(url, modifiers);
			},
			shimUrl: navigationPolicy?.shimUrl,
			domainNamesToReplace: navigationPolicy?.domainNamesToReplace,
			spaceKeyFilter: navigationPolicy?.spaceKeyFilter,
		}),
		[navigationPolicy, dispatch],
	);

	if (!failToLoad) {
		return children({
			iframeRef,
			isLoading: contentLoadStarted && !iframeLoaded,
			iframeLoaded,
			handleIframeOnLoad,
			handleContentLoadStarted,
			handleIframeUnload,
			navigationPolicy: pageNavigationPolicy,
			redirectedToLogin,
		});
	}

	if (!isLoginFlowDisabled) {
		if (shouldAskStorageAccess) {
			return (
				<StorageAccessPage
					url={embeddedConfluenceUrl}
					contentId={contentId}
					spaceKey={spaceKey}
					parentProduct={parentProduct}
				/>
			);
		}
		if (shouldAskToLogin) {
			return (
				<LoginPage
					contentId={contentId}
					parentProduct={parentProduct}
					reloadEP={reloadEP}
					spaceKey={spaceKey}
					url={embeddedConfluenceUrl}
				/>
			);
		}
	}

	return <ErrorScreen standaloneConfluenceUrl={standaloneConfluenceUrl} />;
};
